(function () {
  const SECTION_SELECTOR = ".cd-home-merged";
  const DESKTOP_QUERY = "(min-width: 1025px)";

  const refreshMotionRuntimes = (root) => {
    window.requestAnimationFrame(() => {
      if (window.CDWoodarqMotion && typeof window.CDWoodarqMotion.refresh === "function") {
        window.CDWoodarqMotion.refresh(root);
      }

      if (window.CDCleerGlobalAnimations && typeof window.CDCleerGlobalAnimations.refresh === "function") {
        window.CDCleerGlobalAnimations.refresh();
      }

      if (window.CDHomeAnimationWiring && typeof window.CDHomeAnimationWiring.refresh === "function") {
        window.CDHomeAnimationWiring.refresh(root || document);
      }
    });
  };

  const clamp01 = (value) => Math.max(0, Math.min(1, value));

  const bindStageFade = (root) => {
    if (root.dataset.cdMergedStageFadeBound === "true") return;
    root.dataset.cdMergedStageFadeBound = "true";

    const media = window.matchMedia ? window.matchMedia(DESKTOP_QUERY) : null;
    let frame = null;

    const isDesktop = () => (media ? media.matches : window.innerWidth >= 1025);

    const getFadeTarget = () => {
      if (isDesktop()) {
        return (
          root.querySelector(".cd-home-merged__product-visual") ||
          root.querySelector(".cd-home-merged__product-copy")
        );
      }

      return (
        root.querySelector(".cd-home-merged__product-copy") ||
        root.querySelector(".cd-home-merged__product-mobile") ||
        root.querySelector("[data-product-panel-target]")
      );
    };

    const update = () => {
      frame = null;

      const sectionRect = root.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;

      if (sectionRect.bottom <= 0 || sectionRect.top >= viewportHeight) {
        root.style.setProperty("--cd-merged-stage-opacity", "0");
        return;
      }

      const fadeTarget = getFadeTarget();
      if (!fadeTarget) {
        root.style.setProperty("--cd-merged-stage-opacity", "1");
        return;
      }

      const targetTop = fadeTarget.getBoundingClientRect().top;

      /*
        The stage should disappear gradually while the lower/product area scrolls in.
        Desktop keeps the existing fade timing. Mobile now drives the sticky fixed
        stage box with the same CSS variable instead of leaving it permanently visible.
      */
      const fadeStart = viewportHeight * (isDesktop() ? 1.02 : 0.96);
      const fadeEnd = viewportHeight * (isDesktop() ? 0.18 : 0.34);
      const fadeProgress = clamp01((fadeStart - targetTop) / Math.max(1, fadeStart - fadeEnd));
      const opacity = 1 - fadeProgress;

      root.style.setProperty("--cd-merged-stage-opacity", opacity.toFixed(3));
    };

    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    requestUpdate();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
    window.addEventListener("load", requestUpdate, { once: true });

    if (media) {
      const onMediaChange = () => requestUpdate();
      if (typeof media.addEventListener === "function") {
        media.addEventListener("change", onMediaChange);
      } else if (typeof media.addListener === "function") {
        media.addListener(onMediaChange);
      }
    }
  };

  const TEXT_REVEAL_SELECTOR = [
    ".cd-home-merged__title",
    ".cd-home-merged__description",
    ".cd-home-merged__hero-button",
    ".cd-home-merged__product-title",
    ".cd-home-merged__rating",
    ".cd-home-merged__price-row",
    ".cd-home-merged__product-button",
    ".cd-home-merged__empty",
  ].join(", ");

  const lockRevealedText = (target) => {
    if (target.dataset.cdMergedRevealLocked === "true") return;
    target.dataset.cdMergedRevealLocked = "true";

    // Let the entrance animation finish first, then permanently prevent the
    // Woodarq scroll observer from fading this copy back out at the viewport edge.
    window.setTimeout(() => {
      target.classList.add("cd-home-merged__local-reveal--locked");

      target.querySelectorAll(".cd-wq-word, .cd-wq-char, .cd-wq-space").forEach((node) => {
        node.style.removeProperty("opacity");
        node.style.removeProperty("transform");
        node.style.removeProperty("filter");
      });
    }, 1120);
  };

  const showLocalTextTarget = (target) => {
    target.classList.add("cd-home-merged__local-reveal--visible");
    lockRevealedText(target);
  };

  const bindLocalTextReveal = (root) => {
    const targets = Array.from(root.querySelectorAll(TEXT_REVEAL_SELECTOR)).filter((target) => {
      if (target.dataset.cdMergedLocalRevealBound === "true") return false;
      target.dataset.cdMergedLocalRevealBound = "true";
      return true;
    });

    if (!targets.length) return;

    root.classList.add("cd-home-merged--local-motion");

    targets.forEach((target, index) => {
      target.classList.add("cd-home-merged__local-reveal");
      target.style.setProperty("--cd-merged-local-delay", `${Math.min(index * 90, 360)}ms`);
    });

    if (!("IntersectionObserver" in window)) {
      targets.forEach(showLocalTextTarget);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          showLocalTextTarget(entry.target);
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.22,
        rootMargin: "0px 0px -14% 0px",
      }
    );

    targets.forEach((target) => observer.observe(target));
  };

  const showLocalMediaTarget = (target) => {
    target.classList.add("cd-home-merged__media-reveal--visible");

    window.setTimeout(() => {
      target.classList.add("cd-home-merged__media-reveal--unclipped");
    }, 980);
  };

  const bindLocalMediaReveal = (root) => {
    const targets = Array.from(root.querySelectorAll("[data-cd-merged-media]")).filter((target) => {
      if (target.dataset.cdMergedMediaRevealBound === "true") return false;
      target.dataset.cdMergedMediaRevealBound = "true";
      return true;
    });

    if (!targets.length) return;

    targets.forEach((target, index) => {
      target.style.setProperty("--cd-merged-media-delay", `${Math.min(index * 100 + 80, 280)}ms`);
    });

    if (!("IntersectionObserver" in window)) {
      targets.forEach(showLocalMediaTarget);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          showLocalMediaTarget(entry.target);
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.2,
        rootMargin: "0px 0px -12% 0px",
      }
    );

    targets.forEach((target) => observer.observe(target));
  };

  const stripWoodarqExitFade = (root) => {
    root.querySelectorAll("[data-cd-wq-fade]").forEach((target) => {
      target.removeAttribute("data-cd-wq-fade");
      target.style.opacity = "";
      target.style.transform = "";
      target.style.removeProperty("--cd-copy-opacity");
    });
  };

  const bindRecommendations = (root) => {
    if (!root.hasAttribute("data-product-root")) return;
    if (root.dataset.cdMergedRecommendationsLoaded === "true") return;
    if (root.getAttribute("data-product-performed") === "true") return;

    const url = root.getAttribute("data-product-url");
    if (!url) return;

    root.dataset.cdMergedRecommendationsLoaded = "true";

    fetch(url, {
      credentials: "same-origin",
      headers: {
        Accept: "text/html, application/xhtml+xml",
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const template = document.createElement("template");
        template.innerHTML = html.trim();

        const freshRoot = template.content.querySelector(SECTION_SELECTOR);
        if (!freshRoot) return;

        const freshPanel = freshRoot.querySelector("[data-product-panel-target]");
        const freshVisual = freshRoot.querySelector("[data-product-visual-target]");
        const currentPanel = root.querySelector("[data-product-panel-target]");
        const currentVisual = root.querySelector("[data-product-visual-target]");

        if (currentPanel && freshPanel) currentPanel.innerHTML = freshPanel.innerHTML;
        if (currentVisual && freshVisual) currentVisual.innerHTML = freshVisual.innerHTML;

        root.setAttribute("data-product-performed", "true");

        if (window.CleerdentAjaxCart && typeof window.CleerdentAjaxCart.bind === "function") {
          window.CleerdentAjaxCart.bind(root);
        }

        stripWoodarqExitFade(root);
        bindLocalTextReveal(root);
        bindLocalMediaReveal(root);
        refreshMotionRuntimes(root);
      })
      .catch((error) => {
        root.dataset.cdMergedRecommendationsLoaded = "false";
        console.warn("[Cleerdent merged stage] Product recommendation fallback used.", error);
      });
  };

  const initRoot = (root) => {
    stripWoodarqExitFade(root);
    bindStageFade(root);
    bindLocalTextReveal(root);
    bindLocalMediaReveal(root);
    bindRecommendations(root);
    refreshMotionRuntimes(root);
  };

  const init = () => {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initRoot);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (!event.target) return;
    event.target.querySelectorAll(SECTION_SELECTOR).forEach(initRoot);
  });
})();
